import React, { useEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  Box, Radio, Typography, IconButton, CircularProgress, Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { myQorSavedCards, deleteQorSavedCard } from "../../apollo/server";
import { BrandBadge, brandLabel } from "./cardBrand";

const GET_CARDS = gql`${myQorSavedCards}`;
const DELETE_CARD = gql`${deleteQorSavedCard}`;

/**
 * Saved-card picker for QorCommerce checkout, styled after DoorDash's payment
 * sheet: brand plate, "Visa •••• 1881" with the expiry as a sub-label, a radio
 * on the right, and an "Add a new card" row at the bottom. Selecting a card
 * calls onSelect(token); the parent charges that token directly (no iframe).
 * Selecting "Add a new card" (value "") shows the card iframe instead.
 * `manageOnly` renders just the list + delete (for the account page).
 *
 * The backend dedupes vault duplicates, but we dedupe here too so stale cache
 * or older backends never show "•••• 1881" three times.
 */
export default function QorSavedCards({ selectedToken, onSelect, onCount, manageOnly = false }) {
  const { data, loading, refetch } = useQuery(GET_CARDS, { fetchPolicy: "network-only" });
  const [removeCard, { loading: removing }] = useMutation(DELETE_CARD, {
    onCompleted: () => refetch(),
  });

  const raw = data?.myQorSavedCards ?? [];
  const seen = new Set();
  const cards = raw.filter((c) => {
    const key = `${c.brand || ""}|${c.last4 || ""}|${c.expiry || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Tell the parent how many cards exist (it decides whether to jump straight
  // to the new-card form when the list is empty).
  const count = cards.length;
  useEffect(() => {
    if (!loading && typeof onCount === "function") onCount(count);
  }, [loading, count]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <CircularProgress size={20} sx={{ my: 1 }} />;
  if (cards.length === 0) {
    return manageOnly ? (
      <Typography variant="body2" color="text.secondary">No saved cards yet.</Typography>
    ) : null;
  }

  const rowSx = (selected) => ({
    display: "flex", alignItems: "center", gap: 1.5, py: 1.25, px: 1.5,
    borderRadius: "12px", cursor: onSelect ? "pointer" : "default",
    border: selected ? "2px solid #d9232f" : "1px solid #e5e7eb",
    // Keep row height stable between the 1px and 2px border states.
    m: selected ? 0 : "1px", mb: selected ? 1 : "9px",
    bgcolor: "#fff", transition: "border-color .15s, box-shadow .15s",
    "&:hover": onSelect ? { borderColor: selected ? "#d9232f" : "#cbd5e1", boxShadow: "0 1px 4px rgba(0,0,0,.06)" } : undefined,
  });

  return (
    <Box sx={{ mb: manageOnly ? 0 : 2 }}>
      {!manageOnly && (
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Saved cards
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        {cards.map((c) => {
          const selected = selectedToken === c.token;
          return (
            <Box key={c.token} onClick={() => onSelect && onSelect(c.token)} sx={rowSx(selected)}>
              <BrandBadge brand={c.brand} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#111827", lineHeight: 1.3 }}>
                  {brandLabel(c.brand)} •••• {c.last4 || "----"}
                </Typography>
                {c.expiry && (
                  <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>
                    Expires {c.expiry}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Remove card">
                <span>
                  <IconButton
                    size="small" disabled={removing}
                    onClick={(e) => { e.stopPropagation(); removeCard({ variables: { token: c.token, last4: c.last4 } }); }}
                    sx={{ color: "#9ca3af", "&:hover": { color: "#dc2626" } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {onSelect && <Radio size="small" checked={selected} sx={{ p: 0.5, color: "#d1d5db", "&.Mui-checked": { color: "#d9232f" } }} />}
            </Box>
          );
        })}
        {/* "Add a new card" clears the selection so the card iframe appears. */}
        {!manageOnly && onSelect && (() => {
          const selected = selectedToken === "";
          return (
            <Box onClick={() => onSelect("")} sx={rowSx(selected)}>
              <Box sx={{
                width: 46, height: 30, borderRadius: "6px", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px dashed #cbd5e1", bgcolor: "#f8fafc",
              }}>
                <AddIcon sx={{ fontSize: 18, color: "#64748b" }} />
              </Box>
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.95rem", color: "#111827" }}>
                Add a new card
              </Typography>
              <Radio size="small" checked={selected} sx={{ p: 0.5, color: "#d1d5db", "&.Mui-checked": { color: "#d9232f" } }} />
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
}